import '../portal/portalTheme.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/backendURL';
import { Zap, Sparkles, Shield, CheckCircle2, ArrowRight, Code2, MousePointerClick, Brain, Rocket, Globe, Lock, TrendingUp, ChevronDown, Loader2 } from 'lucide-react';

// Inline Lexa logo (same image as landing page header / Logo.js)
const FunnelLogo = () => (
  <a href="/" className="flex items-center gap-2 group" data-testid="funnel-logo">
    <img
      src="/assets/footer-icon.png"
      alt="Lexa.run"
      className="h-9 w-9 object-contain"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
    <span
      className="text-xl font-bold bg-clip-text text-transparent"
      style={{ backgroundImage: 'linear-gradient(to right, #9333ea, #3b82f6)' }}
    >
      Lexa.run
    </span>
  </a>
);

/**
 * GetStartedFunnel, Funnel #1 (Lead capture → free signup)
 * Path: /get-started
 * NO header/footer/menu. Single goal: capture lead → redirect to signup.
 * Product introduction and lead capture with plan details delegated to Pricing.
 */
export default function GetStartedFunnel() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [appUrl, setAppUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = 'Explore Test Automation | Lexa.run';
  }, []);

  const getUtm = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim()) {
      setError('Please enter your name and work email');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/funnel/lead`, {
        name: name.trim(),
        email: email.trim(),
        app_url: appUrl.trim() || undefined,
        source: 'funnel-get-started',
        ...getUtm(),
      });
      if (data?.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        window.location.href = '/?signup=1';
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Try again.');
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('funnel-lead-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => document.getElementById('funnel-name')?.focus(), 600);
  };

  return (
    <div className="lexa-portal lexa-portal-content min-h-screen antialiased" data-testid="funnel-get-started">
      {/* Minimal top bar, logo only, no nav (Hormozi rule: zero distractions) */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <FunnelLogo />
          <button
            onClick={scrollToForm}
            data-testid="header-cta"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5 transition-all"
          >
            Create My Free Account <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Urgency strip */}
      <div className="bg-gradient-to-r from-purple-600 via-violet-600 to-blue-600 text-white text-center py-2.5 text-sm font-medium">
        <Sparkles className="inline h-4 w-4 mr-1.5 -mt-0.5" />
        Explore Lexa with a free account. See Pricing for current plan limits.
      </div>

      {/* HERO + lead form (Hormozi 2-column hero) */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(124,58,237,0.18),transparent_70%)]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-5 gap-10 items-start">
          {/* LEFT, pitch */}
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700 mb-5">
              <Zap className="h-3.5 w-3.5" /> AI-POWERED TEST AUTOMATION
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Get <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">a Free Lexa Account</span>
              <br />
              Explore Your Testing Workflow
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-slate-600 max-w-2xl">
              Stop writing brittle Selenium scripts. Sign up free, paste your app URL, and describe
              what to test in plain English, Lexa builds tests you can review, edit, and run.
            </p>

            {/* Value stack */}
            <div className="mt-7 space-y-3">
              {[
                ['Free plan', 'on signup, no credit card needed'],
                ['Plain English → real Playwright code', 'edit, run, schedule from day one'],
                ['Full reports + screenshots + video', 'see exactly what passed or failed'],
                ['Browser-based workspace', 'create and organize your tests'],
              ].map(([title, sub], i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`value-prop-${i}`}>
                  <CheckCircle2 className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-800"><b>{title}</b> <span className="text-slate-500">· {sub}</span></p>
                </div>
              ))}
            </div>

          </div>

          {/* RIGHT, lead form */}
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <form
              id="funnel-lead-form"
              onSubmit={handleSubmit}
              data-testid="lead-form"
              className="rounded-3xl border border-slate-200 bg-white p-7 shadow-2xl shadow-purple-500/10 relative overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gradient-to-br from-purple-200/60 to-blue-200/60 blur-3xl pointer-events-none" />

              <div className="relative">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 text-xs font-bold mb-3">
                  <span className="inline-block w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                  FREE · NO CREDIT CARD
                </div>
                <h2 className="text-2xl sm:text-[26px] font-extrabold text-slate-900 leading-tight">
                  Create Your Free Account
                </h2>
                <p className="text-sm text-slate-500 mt-1.5">Start with a free account and explore the product.</p>

                <div className="mt-5 space-y-3">
                  <div>
                    <label htmlFor="funnel-name" className="block text-xs font-semibold text-slate-700 mb-1.5">Your name</label>
                    <input
                      id="funnel-name"
                      data-testid="lead-form-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jane Doe"
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition"
                      autoComplete="name"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="funnel-email" className="block text-xs font-semibold text-slate-700 mb-1.5">Work email</label>
                    <input
                      id="funnel-email"
                      data-testid="lead-form-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="jane@yourcompany.com"
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition"
                      autoComplete="email"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="funnel-url" className="block text-xs font-semibold text-slate-700 mb-1.5">App URL <span className="text-slate-400 font-normal">(optional, we'll tailor your tests)</span></label>
                    <input
                      id="funnel-url"
                      data-testid="lead-form-url"
                      type="url"
                      value={appUrl}
                      onChange={(e) => setAppUrl(e.target.value)}
                      placeholder="https://yourapp.com"
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition"
                    />
                  </div>
                </div>

                {error && (
                  <p data-testid="lead-form-error" className="mt-3 text-sm text-rose-600">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-form-submit"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Continuing to signup…</>
                  ) : (
                    <>Create My Free Account <ArrowRight className="h-5 w-5" /></>
                  )}
                </button>

                <p className="text-[11px] text-slate-400 mt-3 text-center leading-relaxed">
                  <Lock className="inline h-3 w-3 mr-1 -mt-0.5" />
                  No spam. No credit card. Cancel anytime. We never share your data.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS, 3 steps */}
      <section className="bg-white border-y border-slate-200 py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            From Zero to Running Tests in 3 Steps
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Create a workspace, review your tests, and inspect execution results.
          </p>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: MousePointerClick, title: 'Sign up free', desc: 'Create your workspace and explore the free plan.', color: 'from-purple-500 to-pink-500' },
              { icon: Brain, title: 'Paste your URL + describe in English', desc: 'Lexa AI converts your plain English into a full Playwright test suite, selectors, assertions, screenshots, the works.', color: 'from-blue-500 to-cyan-500' },
              { icon: Rocket, title: 'Run, schedule, repeat', desc: 'Execute on real browsers. Get reports, videos, console logs. Plug into CI/CD when ready.', color: 'from-emerald-500 to-green-500' },
            ].map((step, i) => (
              <div key={i} className="relative rounded-3xl border border-slate-200 bg-white p-7 text-left hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="absolute -top-3 left-7 text-xs font-bold tracking-widest text-slate-400">STEP {i+1}</div>
                <div className={`mt-2 w-12 h-12 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center text-white shadow-lg`}>
                  <step.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-bold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 text-center tracking-tight">Frequently Asked Questions</h2>
          <div className="mt-10 space-y-3">
            {[
              { q: 'Is there a free plan?', a: 'Yes. Visit Pricing for the current free plan and its limits. You can explore the product before choosing a paid plan.' },
              { q: 'Do I need to review AI-generated tests?', a: 'Yes. Review generated steps, assertions, and results to make sure they match your application and requirements.' },
              { q: 'What can I organize in a workspace?', a: 'Lexa includes API tests, browser flows, projects, test suites, and Sprint QA.' },
            ].map((item, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between text-left px-5 py-4 font-semibold text-slate-900 hover:bg-slate-50 transition"
                >
                  <span>{item.q}</span>
                  <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-slate-600 leading-relaxed">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-violet-600 to-blue-600 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            Your testing workspace is <br className="hidden sm:block" /> one click away.
          </h2>
          <p className="mt-4 text-purple-100 text-lg">No credit card. No install. No catch.</p>
          <button onClick={scrollToForm} data-testid="final-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-purple-700 px-8 py-4 text-base font-bold shadow-xl hover:-translate-y-0.5 hover:shadow-2xl transition-all">
            Create My Free Account <ArrowRight className="h-5 w-5" />
          </button>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-purple-100">
            <span className="inline-flex items-center gap-1.5"><Shield className="h-4 w-4" /> Workspace access controls</span>
            <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4" /> Browser-based workspace</span>
            <span className="inline-flex items-center gap-1.5"><Code2 className="h-4 w-4" /> Real Playwright code</span>
            <span className="inline-flex items-center gap-1.5"><TrendingUp className="h-4 w-4" /> Execution reports</span>
          </div>
        </div>
      </section>

      {/* Minimal legal footer */}
      <footer className="bg-slate-950 text-slate-400 text-xs py-6 text-center px-4">
        © {new Date().getFullYear()} Lexa.run · <a href="/privacy" className="hover:text-white">Privacy</a> · <a href="/terms" className="hover:text-white">Terms</a>
      </footer>
    </div>
  );
}
